/**
 * ALBFICHA-a · ficha de albergue del plan (Anexo VI «Lugares de albergue y almacenamiento»).
 *
 * La plantilla oficial del PTEL trae por fila, además del nombre, el aforo, la dirección y las
 * coordenadas: Servicios básicos (electricidad, agua, generador) · Higiene (n.º de inodoros,
 * lavabos y duchas) · Capacidad (área abierta y cerrada en m², n.º de habitaciones) ·
 * Equipamientos (n.º de camas y mantas, estanterías, otros). Hasta ahora el extractor lo tiraba.
 *
 * Función pura: recibe los párrafos de cada celda de la fila ANTES de que `shouldExpandRow`
 * la trocee (51 de 155 filas del corpus se trocean y la ficha acababa en filas fantasma).
 * Las celdas se reconocen por sus etiquetas, no por la posición de la columna.
 *
 * DOP selladas por Luis (29-sep-2026, «adelante»): captura máxima; los servicios solo cuentan
 * si dicen «Sí/No» o el autor borró la línea (la plantilla sin tocar no dice nada); plantilla
 * vacía → sin dato, «0» escrito → 0; «1.910» = 1910 y «236,70» = 236,7; el texto que no es
 * una cifra limpia se guarda como nota; el área abierta es dato del recinto, nunca plazas.
 */

export type CampoNumericoFicha =
  | 'inodoros' | 'lavabos' | 'duchas'
  | 'areaAbiertaM2' | 'areaCerradaM2' | 'habitaciones'
  | 'camas' | 'mantas' | 'estanterias';

export interface FichaAlbergue {
  electricidad?: boolean;
  agua?: boolean;
  generador?: boolean;
  inodoros?: number;
  lavabos?: number;
  duchas?: number;
  /** Superficie al aire libre del recinto: dato aparte, nunca cuenta como plazas. */
  areaAbiertaM2?: number;
  /** Superficie cubierta: la única que usa la regla DGPC (÷ 10 m²/persona). */
  areaCerradaM2?: number;
  habitaciones?: number;
  camas?: number;
  mantas?: number;
  estanterias?: number;
  otros?: string;
  /** Texto libre de la casilla «Equipamientos» sin etiqueta («Equipamiento deportivo»). */
  equipamientos?: string;
  /** Texto del plan en los campos numéricos que no eran una cifra limpia («20 aulas», «Si»). */
  notas?: Partial<Record<CampoNumericoFicha, string>>;
}

type CampoTexto = 'otros';
type CampoEtiqueta = CampoNumericoFicha | CampoTexto;

/** Etiquetas de la plantilla. `(?![\p{L}])` evita «Aguamarga» o «duchasX». */
const RE_ETIQUETA = new RegExp(
  '(n\\s*\\.?\\s*[º°]\\s*)?'
  + '(inodoros?|lavabos?|duchas?|[áa]rea\\s*abierta|[áa]rea\\s*cerrada|habitaciones|camas|mantas|estanter[íi]as?|otros)'
  + '(?![\\p{L}])',
  'giu',
);

function campoDe(etiqueta: string): CampoEtiqueta {
  const e = etiqueta.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  if (e.startsWith('inodoro')) return 'inodoros';
  if (e.startsWith('lavabo')) return 'lavabos';
  if (e.startsWith('ducha')) return 'duchas';
  if (/area\s*abierta/.test(e)) return 'areaAbiertaM2';
  if (/area\s*cerrada/.test(e)) return 'areaCerradaM2';
  if (e.startsWith('habitaciones')) return 'habitaciones';
  if (e.startsWith('camas')) return 'camas';
  if (e.startsWith('mantas')) return 'mantas';
  if (e.startsWith('estanter')) return 'estanterias';
  return 'otros';
}

interface Etiqueta { campo: CampoEtiqueta; inicio: number; fin: number }

/**
 * Una etiqueta vale si abre el párrafo (tras rellenos), si lleva «n.º» delante o si le sigue
 * «:». Así «OTROS: Estanterías, otros.» da una sola etiqueta («otros») y no tres.
 */
function etiquetasDe(parrafo: string): Etiqueta[] {
  const out: Etiqueta[] = [];
  for (const m of parrafo.matchAll(RE_ETIQUETA)) {
    const inicio = m.index ?? 0;
    const fin = inicio + m[0].length;
    const abre = /^[\s_.\-–·•]*$/.test(parrafo.slice(0, inicio));
    const conNumero = Boolean(m[1]);
    const conDosPuntos = /^\s*:/.test(parrafo.slice(fin));
    if (abre || conNumero || conDosPuntos) out.push({ campo: campoDe(m[2]), inicio, fin });
  }
  return out;
}

/** «1.910» → 1910 · «236,70» → 236,7 · «1292» → 1292 · «12.5» → 12,5. */
function aNumero(t: string): number {
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(t)) return Number(t.replace(/\./g, '').replace(',', '.'));
  return Number(t.replace(',', '.'));
}

const RE_CIFRA_INICIAL = /^(\d{1,3}(?:\.\d{3})+(?:,\d+)?|\d+(?:[.,]\d+)?)/;

/** Valor de un campo numérico a partir del texto que sigue a su etiqueta. */
export function leerCifraFicha(bruto: string): { valor?: number; nota?: string } {
  const texto = bruto.replace(/_+/g, ' ').replace(/^[\s:.\-–]+/, '').replace(/\s+/g, ' ').trim();
  const sinUnidad = texto.replace(/(?<![\p{L}])m\s*[2²](?![\p{L}\d])/giu, ' ').replace(/\s+/g, ' ')
    .replace(/[\s.:;,]+$/, '').trim();
  if (!sinUnidad) return {};
  const m = sinUnidad.match(RE_CIFRA_INICIAL);
  const nota = texto.replace(/[\s.:;,]+$/, '').slice(0, 200);
  if (!m) return /[\p{L}\d]/u.test(sinUnidad) ? { nota } : {};
  const resto = sinUnidad.slice(m[0].length).trim();
  if (!resto) return { valor: aNumero(m[0]) };
  // Una enumeración («1 comedor; 1 salón…») no es un número: se guarda el texto.
  if (resto.includes(';')) return { nota };
  return { valor: aNumero(m[0]), nota };
}

function limpiarTexto(t: string): string | undefined {
  const s = t.replace(/_+/g, ' ').replace(/^[\s:.\-–]+/, '').replace(/\s+/g, ' ').trim();
  return /[\p{L}\d]/u.test(s) ? s.slice(0, 300) : undefined;
}

type Servicio = 'electricidad' | 'agua' | 'generador';
const SERVICIOS: readonly Servicio[] = ['electricidad', 'agua', 'generador'];
/** Un párrafo de servicio es solo la palabra y, si acaso, «Sí/No» (o «X», «✓»). */
const RE_SERVICIO = /^[\s\-–·•]*(electricidad|agua|generador)(?:\s*[:\-]?\s*(s[ií]|no|x|✓))?[\s.]*$/iu;

function leerServicios(parrafos: string[]): Pick<FichaAlbergue, Servicio> {
  const vistos = new Map<Servicio, boolean | null>();
  for (const p of parrafos) {
    const m = p.match(RE_SERVICIO);
    if (!m) continue;
    const s = m[1].toLowerCase() as Servicio;
    const v = m[2]?.toLowerCase();
    vistos.set(s, v == null ? null : v !== 'no');
  }
  if (vistos.size === 0) return {};
  const out: Pick<FichaAlbergue, Servicio> = {};
  const explicitos = [...vistos.values()].some(v => v !== null);
  if (explicitos) {
    for (const [s, v] of vistos) if (v !== null) out[s] = v;
    return out;
  }
  // Solo palabras: si están las tres es la plantilla sin tocar y no dice nada;
  // si el autor borró alguna, las que quedan están y la borrada no.
  if (vistos.size === SERVICIOS.length) return {};
  for (const s of SERVICIOS) out[s] = vistos.has(s);
  return out;
}

const CAMPOS_EQUIPAMIENTO: ReadonlySet<CampoEtiqueta> = new Set(['camas', 'mantas', 'estanterias', 'otros']);
const CAMPOS_CAPACIDAD: ReadonlySet<CampoEtiqueta> = new Set(['areaAbiertaM2', 'areaCerradaM2', 'habitaciones']);

/**
 * Lee la ficha de albergue de una fila (párrafos por celda). Devuelve `undefined` si la fila
 * no es una ficha (menos de dos campos de la plantilla) o si la ficha viene sin rellenar.
 */
export function extraerFichaAlbergue(celdas: string[][]): FichaAlbergue | undefined {
  const porCelda = celdas.map(ps => ps.map(p => ({ p, et: etiquetasDe(p) })));
  const distintos = new Set(porCelda.flat().flatMap(x => x.et.map(e => e.campo)));
  if (distintos.size < 2) return undefined;

  const ficha: FichaAlbergue = {};
  const notas: Partial<Record<CampoNumericoFicha, string>> = {};
  for (const celda of porCelda) {
    for (const { p, et } of celda) {
      et.forEach((e, k) => {
        if (e.campo in ficha || (e.campo !== 'otros' && e.campo in notas)) return; // primera aparición manda
        const bruto = p.slice(e.fin, k + 1 < et.length ? et[k + 1].inicio : p.length);
        if (e.campo === 'otros') {
          const t = limpiarTexto(bruto);
          if (t) ficha.otros = t;
          return;
        }
        const { valor, nota } = leerCifraFicha(bruto);
        if (valor !== undefined && Number.isFinite(valor)) ficha[e.campo] = valor;
        if (nota) notas[e.campo] = nota;
      });
    }
  }

  // Equipamientos: texto sin etiqueta de la casilla de camas/mantas/otros o, si no la hay,
  // de la casilla que sigue a la de capacidad («Equipamiento deportivo»), solo si detrás
  // aún queda una casilla con texto (la dirección). Si el autor borró la columna (Cortes y
  // Graena), la casilla siguiente ya es la dirección y detrás solo hay coordenadas.
  const idx = (campos: ReadonlySet<CampoEtiqueta>) =>
    porCelda.findIndex(c => c.some(x => x.et.some(e => campos.has(e.campo))));
  let iEquip = idx(CAMPOS_EQUIPAMIENTO);
  if (iEquip < 0) {
    const iCap = idx(CAMPOS_CAPACIDAD);
    const siguienteLibre = iCap >= 0 && iCap + 1 < porCelda.length && porCelda[iCap + 1].every(x => x.et.length === 0);
    const direccionDetras = porCelda.slice(iCap + 2).some(c => c.some(x => /\p{L}{3,}/u.test(x.p)));
    if (siguienteLibre && direccionDetras) iEquip = iCap + 1;
  }
  if (iEquip >= 0) {
    const libres = porCelda[iEquip].filter(x => x.et.length === 0).map(x => limpiarTexto(x.p)).filter(Boolean);
    if (libres.length) ficha.equipamientos = libres.join(' · ').slice(0, 300);
  }

  Object.assign(ficha, leerServicios(celdas.flat()));
  if (Object.keys(notas).length) ficha.notas = notas;
  return Object.keys(ficha).length ? ficha : undefined;
}

/** Cifra con la norma de la RAE: coma decimal; espacio fino solo a partir de cinco cifras. */
export function cifraRAE(n: number): string {
  const [ent, dec] = String(Math.round(n * 100) / 100).split('.');
  const entero = ent.length >= 5 ? ent.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F') : ent;
  return dec ? `${entero},${dec}` : entero;
}

const cuenta = (n: number, sing: string, plur: string) => `${cifraRAE(n)} ${n === 1 ? sing : plur}`;
const nota = (f: FichaAlbergue, c: CampoNumericoFicha) => f.notas?.[c] ? `«${f.notas[c]}»` : undefined;

/**
 * Líneas legibles de la ficha para P2 (y, en `ALBFICHA-b`, para la exportación). Solo los
 * campos con dato; un campo sin cifra pero con texto del plan se muestra entre comillas.
 */
export function lineasFichaAlbergue(f: FichaAlbergue): string[] {
  const out: string[] = [];
  const sup: string[] = [];
  if (f.areaCerradaM2 !== undefined) sup.push(`cerrada ${cifraRAE(f.areaCerradaM2)} m²`);
  else if (nota(f, 'areaCerradaM2')) sup.push(`cerrada ${nota(f, 'areaCerradaM2')}`);
  if (f.areaAbiertaM2 !== undefined) sup.push(`abierta ${cifraRAE(f.areaAbiertaM2)} m² (del recinto, no cuenta como plazas)`);
  if (sup.length) out.push(`Superficie: ${sup.join(' · ')}`);

  const aseos: string[] = [];
  const higiene: Array<[CampoNumericoFicha, string, string]> = [
    ['inodoros', 'inodoro', 'inodoros'], ['lavabos', 'lavabo', 'lavabos'], ['duchas', 'ducha', 'duchas']];
  for (const [c, s, p] of higiene) {
    const v = f[c];
    if (v !== undefined) aseos.push(cuenta(v, s, p));
    else if (nota(f, c)) aseos.push(`${p} ${nota(f, c)}`);
  }
  if (aseos.length) out.push(`Aseos: ${aseos.join(' · ')}`);

  const aloj: string[] = [];
  const dotacion: Array<[CampoNumericoFicha, string]> = [
    ['habitaciones', 'habitaciones'], ['camas', 'camas'], ['mantas', 'mantas'], ['estanterias', 'estanterías']];
  for (const [c, rotulo] of dotacion) {
    const v = f[c];
    if (v !== undefined) aloj.push(`${rotulo} ${cifraRAE(v)}`);
    else if (nota(f, c)) aloj.push(`${rotulo} ${nota(f, c)}`);
  }
  if (aloj.length) out.push(`Dotación: ${aloj.join(' · ')}`);

  const serv = SERVICIOS.filter(s => f[s] !== undefined).map(s => `${s} ${f[s] ? 'sí' : 'no'}`);
  if (serv.length) out.push(`Servicios: ${serv.join(' · ')}`);
  if (f.equipamientos) out.push(`Equipamiento: ${f.equipamientos}`);
  if (f.otros) out.push(`Otros: ${f.otros}`);
  return out;
}
